import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { WeatherHistoryEntryDto } from '../../api/weather.js';
import type { TemperatureUnit } from '../../classroomPages/temperatureUnitStorage.js';
import { WeatherCharts } from './WeatherCharts.js';
import styles from './WeatherHistoryModal.module.css';

export interface WeatherHistoryModalProps {
  history: WeatherHistoryEntryDto[];
  unit: TemperatureUnit;
  onClose: () => void;
}

/**
 * Pops the monthly Weather history into its own dialog so the live
 * Weather page can stay calm (just the picker + thermometer). Same
 * close affordances as the Calendar day-detail modal: tap backdrop,
 * close button, or press Escape.
 */
export function WeatherHistoryModal({ history, unit, onClose }: WeatherHistoryModalProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Render via portal so the modal mounts on document.body, outside
  // the KioskStage's `transform: scale(...)` container. Without this,
  // position: fixed inside a transformed ancestor resolves against
  // the transformed box (the stage), not the real viewport — which
  // made the modal far smaller than 95vh and forced inner scrolling.
  return createPortal(
    <div
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="weather-history-title"
      data-testid="weather-history-modal"
      onClick={onClose}
    >
      <article
        className={`glass glass--solid ${styles.panel}`}
        onClick={(e) => e.stopPropagation()}
        role="document"
      >
        <header className={styles.header}>
          <h2 id="weather-history-title" className={styles.heading}>
            Weather history
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={styles.close}
            data-testid="weather-history-close"
          >
            ×
          </button>
        </header>

        <WeatherCharts history={history} unit={unit} />
      </article>
    </div>,
    document.body,
  );
}
